import { createTheme } from '@mui/material/styles';
import LinkBehavior from '../LinkBehavior';
import { colors } from '../../theme';

const hubTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      x200: 200,
      sm: 420,
      x400: 400,
      x600: 600,
      md: 768,
      x800: 800,
      x1000: 1000,
      x1200: 1200,
      lg: 1200,
      x1400: 1400,
      xl: 1536,
      x1600: 1600,
      x1800: 1800,
    },
  },
  palette: {
    ...colors,
    tooltip: {
      fill: '#3700B3E5',
    },
    error: {
      main: '#B00020',
      light: '#D32F2F',
    },
    disabled: {
      textContrastLight: 'rgba(0, 0, 0, 0.38)',
    },
    info: {
      main: '#0288D1',
    },
    warning: {
      main: 'rgba(237, 108, 2, 1)',
    },
    alert: {
      warningFill: 'rgba(253, 240, 230, 1)',
    },
  },
  typography: {
    fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.5,
    },
    h2: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.5,
    },
    h3: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.5,
    },
    h4: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      fontSize: '1.875rem',
      lineHeight: 1.235,
    },
    h5: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.25,
    },
    h6: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: 1.6,
      letterSpacing: 0,
    },
    subtitle1: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.75,
    },
    subtitle2: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.57,
    },
    body1: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938rem',
    },
    body2: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    caption: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
    overline: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontSize: '0.625rem',
      fontWeight: 500,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: colors.primary.textBlack,
    },
    sectionTitle: {
      fontFamily: '"Rasa", "Helvetica", "Arial", sans-serif',
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: '0.00938rem',
    },
    sectionTitleBold: {
      fontFamily: '"Lexend", "Helvetica", "Arial", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '0.00938rem',
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
        invisible: {
          backgroundColor: 'rgba(0, 0, 0, 0)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          ':last-child': {
            paddingBottom: '1rem',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          letterSpacing: 0,
        },
        sizeSmall: {
          fontSize: '13px',
        },
        sizeLarge: {
          fontSize: '15px',
        },
        contained: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText || '#ffffff',
          boxShadow: theme.shadows[2],
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: theme.shadows[4],
          },
          '&:active': {
            boxShadow: theme.shadows[8],
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
            boxShadow: 'none',
          },
          width: '100%',
          borderRadius: '8px',
          fontSize: '15px',
          fontWeight: 500,
          padding: '8px 22px',
          lineHeight: '26px',
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '26px',
          letterSpacing: 0,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          sectionTitle: 'h2',
          sectionTitleBold: 'h2',
        },
      },
    },
  },
});

export default hubTheme;
